import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, MapPin } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import TempleCard from '../components/cards/TempleCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');

  const categories = [
    { id: 'all', name: 'All Temples' },
    { id: 'char_dham', name: 'Char Dham' },
    { id: 'panch_kedar', name: 'Panch Kedar' },
    { id: 'shakti_peeth', name: 'Shakti Peeths' },
    { id: 'other', name: 'Other Sacred' },
  ];

  useEffect(() => {
    const fetchTemples = async () => {
      setLoading(true);
      try {
        const category = activeCategory === 'all' ? '' : activeCategory;
        const response = await axios.get(`${API}/temples`, {
          params: category ? { category } : {}
        });
        setTemples(response.data);
      } catch (error) {
        console.error('Error fetching temples:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemples();
  }, [activeCategory]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const filteredTemples = temples.filter(temple =>
    temple.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    temple.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white pt-24">
        {/* ═══════════════════ PREMIUM HERO BANNER ═══════════════════ */}
        <div className="relative h-[65vh] min-h-[500px] flex items-center justify-center overflow-hidden mb-12">
          {/* Parallax Background */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1577516311194-eb14c570a137?q=80&w=2000"
              alt="Himalayas"
              className="w-full h-full object-cover"
            />
            {/* Rich Gradients overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a1128]/80 via-[#0a1128]/50 to-[#0a1128]/80"></div>
            <div className="absolute inset-0 bg-gradient-radial from-[#4A6274]/30 to-transparent flex-shrink-0 blur-[80px]"></div>
          </div>
          
          <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-md mb-8 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
                <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse"></span>
                <span className="text-white font-bold text-xs uppercase tracking-[0.3em] font-heading">Divine Blessings</span>
                <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse"></span>
              </div>
              
              <h1 className="font-heading text-5xl md:text-7xl font-black text-white mb-6 tracking-tight drop-shadow-2xl">
                Sacred <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-white">Temples</span>
              </h1>
              <p className="text-blue-100/90 text-lg sm:text-xl font-medium max-w-2xl mx-auto mb-12 drop-shadow-lg leading-relaxed">
                Book authentic online pujas at India's most sacred temples. From Kedarnath to Kamakhya, divine blessings await.
              </p>

              {/* Luxury Search Bar */}
              <div className="max-w-2xl mx-auto relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-[#D4AF37] rounded-full blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
                <div className="relative flex items-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-full pl-6 pr-2 py-2 shadow-2xl transition-all">
                  <Search className="w-6 h-6 text-blue-200 opacity-80" />
                  <Input
                    placeholder="Search temples by name or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent border-0 text-white placeholder-blue-100/60 focus:ring-0 text-lg px-4 h-14"
                    data-testid="temple-search-input"
                  />
                  <button className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 h-12 rounded-full font-bold shadow-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all">
                    Search
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Filters */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`category-pill ${activeCategory === category.id ? 'active' : ''}`}
                data-testid={`category-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Results Count */}
          <p className="text-[#718096] mb-6">
            Showing {filteredTemples.length} temple{filteredTemples.length !== 1 ? 's' : ''}
          </p>

          {/* Temple Grid */}
          <div className="temple-grid">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-96 animate-pulse"></div>
              ))
            ) : filteredTemples.length > 0 ? (
              filteredTemples.map((temple, index) => (
                <TempleCard key={temple.id} temple={temple} index={index} />
              ))
            ) : (
              <div className="col-span-full text-center py-16">
                <MapPin className="w-16 h-16 text-[#D1D5DB] mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#4A5568] mb-2">
                  No temples found
                </h3>
                <p className="text-[#718096]">
                  Try adjusting your search or filters
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TemplesPage;
